import { format } from 'date-fns';
import { AxiosError } from 'axios';
import type { MRT_SortingState, MRT_PaginationState } from 'material-react-table';
import {
  PaginatedResult,
  createUseApiQuery,
  createUseApiMutation,
  createUsePublicApiQuery,
  createUsePublicApiMutation,
} from '@emfood/shared';

import { GridPaginationModel } from '@mui/x-data-grid';
import { GridSortItem } from '@mui/x-data-grid/models/gridSortModel';

import { SearchAndFilterData } from 'src/hooks/use-product-list';
import { SearchAndFilterFormData } from 'src/hooks/use-products.list';

import { Role } from '../models/roles';
import { NewsPostModel } from '../models/news';
import { getActivePrice } from '../models/surplus';
import { Endpoint, endpoints } from '../api-config';
import { RegisterUserRequest } from '../models/users';
import { InvoiceDetailsModel } from '../models/invoices';
import { AttributesResponse } from '../models/attributes';
import { ProductImagesResponse } from '../models/product-images';
import { GenerationHistoryItem } from '../models/generation-history';
import { Distributor, DistributorSite } from '../models/distributors';
import { IProducerDetails, ProducerListItem } from '../models/producer';
import { ProductListModel, ProductDetailsModel } from '../models/products';
import { RecommendationItemModel } from '../models/recommendations';
import { SeasonalCalendarItem, SeasonalCalendarSearchRequest } from '../models/seasonal.calendar';
import {
  FinanceOrderListModel,
  FinanceFilterOptionsModel,
  FinanceOrdersListFilterModel,
} from '../models/finance';
import {
  BuyerSitesResponseModel,
  BuyerSiteDetailsResponseModel,
  BuyerSiteLinkedSiteResponseModel,
} from '../models/buyers';
import {
  BuyerOrderModel,
  OrderIssueModel,
  OrderIssueLogModel,
  CreateOrderIssueReplyModel,
  CreateOrderIssueRequestModel,
  UpdateOrderIssueRequestModel,
  UploadOrderIssueImageResponse,
} from '../models/orders';
import {
  CheckoutResponse,
  CartResponseModel,
  ValidateCartResponse,
  AddToCartRequestModel,
  EditOrderResponseModel,
  UpdateBasketRequestModel,
  UpdateQuantityRequestModel,
} from '../models/cart';
import {
  FavouritesDataModel,
  NewFavouritesItemRequestModel,
  NewFavouritesCollectionRequestModel,
  ControlFavouritesAccessRequestModel,
  UpdateFavouritesCollectionRequestModel,
} from '../models/favourites';

export const useApiQuery = createUseApiQuery<Endpoint>();
export const useApiMutation = createUseApiMutation<Endpoint>();

// Pre-session flows (registration, password/MFA recovery, anonymous email-link replies) use these
// so the API interceptor exempts them from the no-session force-logout. See createUsePublicApi*.
export const usePublicApiQuery = createUsePublicApiQuery<Endpoint>();
export const usePublicApiMutation = createUsePublicApiMutation<Endpoint>();

export const useGetBuyerSiteDetails = (buyerOrgId: string, buyerSiteId: string, enabled: boolean) =>
  useApiQuery<BuyerSiteDetailsResponseModel>(
    ['buyerSite', buyerOrgId, buyerSiteId],
    endpoints.getBuyerSiteDetails,
    'get',
    {
      pathParams: {
        buyerOrgId,
        buyerSiteId,
      },
      enabled: enabled && !!buyerOrgId && !!buyerSiteId,
      meta: {
        description: 'fetch buyer site details',
      },
    }
  );

export const useGetBuyerSites = (buyerOrgId: string | null) =>
  useApiQuery<BuyerSitesResponseModel>(['buyerSites', buyerOrgId], endpoints.getBuyerSites, 'get', {
    pathParams: {
      buyerOrgId,
    },
    enabled: !!buyerOrgId,
    meta: {
      description: 'fetch buyer sites',
    },
  });

export const useGetBuyerSiteLinkedSites = (
  buyerOrgId: string,
  buyerSiteId: string,
  enabled: boolean
) =>
  useApiQuery<BuyerSiteLinkedSiteResponseModel[]>(
    ['linkedSites', buyerOrgId, buyerSiteId],
    endpoints.getBuyerSiteLinkedSites,
    'get',
    {
      refetchOnWindowFocus: false,
      pathParams: {
        buyerOrgId,
        buyerSiteId,
      },
      enabled: enabled && !!buyerOrgId && !!buyerSiteId,
      meta: {
        description: 'fetch buyer linked sites',
      },
    }
  );

export const useGetAllProducts = (
  paginationModel: GridPaginationModel,
  sortModel?: GridSortItem,
  filterSearchModel?: SearchAndFilterFormData
) => {
  let countriesOfOriginFilter = filterSearchModel?.countryOfOrigin;

  // Automatically include constitute countries in United Kingdom
  if (countriesOfOriginFilter?.includes('United Kingdom')) {
    countriesOfOriginFilter = [...(filterSearchModel?.countryOfOrigin ?? [])];
    countriesOfOriginFilter.push('England', 'Scotland', 'Wales', 'Northern Ireland');
  }

  return useApiQuery<PaginatedResult<ProductListModel>>(
    ['products', paginationModel.page, paginationModel.pageSize, filterSearchModel, sortModel],
    endpoints.searchProducts,
    'post',
    {
      refetchOnWindowFocus: false,
      body: {
        searchField: filterSearchModel?.productName,
        availableFrom:
          filterSearchModel?.availableFrom &&
          !Number.isNaN(filterSearchModel.availableFrom.getTime())
            ? format(
                new Date(filterSearchModel.availableFrom.setHours(0, 0, 0, 0)),
                "yyyy-MM-dd'T'23:59:59.999'Z'"
              )
            : undefined,
        categories: filterSearchModel?.categories,
        freeFrom: filterSearchModel?.allergens,
        excludeAllergens: true,
        producerDistance: filterSearchModel?.producerDistance,
        certifications: filterSearchModel?.certifications,
        dietaryLifestyles: filterSearchModel?.dietaryLifestyles,
        countriesOfOrigin: countriesOfOriginFilter,
        distributorSiteId: filterSearchModel?.distributorSiteId,
        airfreighted: filterSearchModel?.airfreighted,
        sortBy: sortModel?.field,
        sortOrder: sortModel?.sort?.toUpperCase(),
        productType: filterSearchModel?.productType,
      },
      queryParams: {
        // +1 because datagrid is 0 based and backend starts from 1
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      },
      meta: {
        description: 'fetch all products',
      },
      select: (response) => ({
        ...response,
        data: response.data.map((entry) => ({
          ...entry,
          activePrice: getActivePrice(entry.surplusEntries, entry.price),
        })),
      }),
    }
  );
};

export const useGetAllProductsMRT = (
  paginationModel: MRT_PaginationState,
  sortModel?: MRT_SortingState[0],
  filterSearchModel?: SearchAndFilterData
) => {
  let countriesOfOriginFilter = filterSearchModel?.countryOfOrigin;

  // Automatically include constitute countries in United Kingdom
  if (countriesOfOriginFilter?.includes('United Kingdom')) {
    countriesOfOriginFilter = [...(filterSearchModel?.countryOfOrigin ?? [])];
    countriesOfOriginFilter.push('England', 'Scotland', 'Wales', 'Northern Ireland');
  }

  return useApiQuery<PaginatedResult<ProductListModel>>(
    ['products', paginationModel.pageIndex, paginationModel.pageSize, filterSearchModel, sortModel],
    endpoints.searchProducts,
    'post',
    {
      refetchOnWindowFocus: false,
      body: {
        searchField: filterSearchModel?.productName,
        availableFrom:
          filterSearchModel?.availableFrom &&
          !Number.isNaN(filterSearchModel.availableFrom.getTime())
            ? format(
                new Date(filterSearchModel.availableFrom.setHours(0, 0, 0, 0)),
                "yyyy-MM-dd'T'23:59:59.999'Z'"
              )
            : undefined,
        categories: filterSearchModel?.categories,
        freeFrom: filterSearchModel?.allergens,
        excludeAllergens: true,
        producerDistance: filterSearchModel?.producerDistance,
        certifications: filterSearchModel?.certifications,
        dietaryLifestyles: filterSearchModel?.dietaryLifestyles,
        countriesOfOrigin: countriesOfOriginFilter,
        distributorSiteId: filterSearchModel?.distributorSiteId,
        airfreighted: filterSearchModel?.airfreighted,
        sortBy: sortModel?.id,
        sortOrder: sortModel?.desc ? 'DESC' : 'ASC',
        productType: filterSearchModel?.productType,
        favouriteCollectionIds: filterSearchModel?.favouriteCollectionIds?.length
          ? filterSearchModel.favouriteCollectionIds
          : undefined,
      },
      queryParams: {
        // +1 because MRT is 0 based and backend starts from 1
        page: paginationModel.pageIndex + 1,
        pageSize: paginationModel.pageSize,
      },
      meta: {
        description: 'fetch all products',
      },
      select: (response) => ({
        ...response,
        data: response.data.map((entry) => ({
          ...entry,
          activePrice: getActivePrice(entry.surplusEntries, entry.price),
        })),
      }),
    }
  );
};

export const useGetAllAttributes = () =>
  useApiQuery<AttributesResponse>(['attributes'], endpoints.getAllAttributes, 'get', {
    staleTime: Infinity,
    meta: {
      description: 'fetch all attributes',
    },
  });

export const useAttributes = () =>
  useApiQuery<AttributesResponse>(['attributes'], endpoints.getAttributes, 'get', {
    queryParams: {
      attributesTypes: [3, 14, 4, 6, 1].toString(),
    },
    staleTime: Infinity,
    select: (data) => {
      const allowedAllergens = [
        'Celery',
        'Crustacea',
        'Eggs',
        'Fish',
        'Lupin',
        'Milk',
        'Molluscs',
        'Mustard',
        'Peanuts',
        'Sesame Seeds',
        'Soya',
        'Sulphur Dioxide',
        'Tree Nuts',
        'Gluten',
      ];

      return {
        ...data,
        attributes: data.attributes.map((attributeGroup) => {
          if (attributeGroup.attributeType === 1) {
            return {
              ...attributeGroup,
              attributes: attributeGroup.attributes.filter((attr) =>
                allowedAllergens.includes(attr.name)
              ),
            };
          }
          return attributeGroup;
        }),
      };
    },
    meta: {
      description: 'fetch attributes',
    },
  });

export const useGetAllRoles = () =>
  useApiQuery<Role[]>(['roles'], endpoints.getAllRoles, 'get', {
    meta: {
      description: 'fetch all roles',
    },
  });

export const useGetFavouritesCollections = (page: number, pageSize: number) =>
  useApiQuery<FavouritesDataModel>(
    ['favourites', page, pageSize],
    endpoints.getListOfFavouritesCollections,
    'get',
    {
      refetchOnWindowFocus: false,
      queryParams: {
        page,
        pageSize,
      },
      refetchOnMount: true,
      meta: {
        description: 'fetch favourites lists',
      },
    }
  );

export const useAddFavouritesCollection = () =>
  useApiMutation<void, Error, NewFavouritesCollectionRequestModel>(
    endpoints.addFavouritesCollection,
    'post',
    {
      meta: {
        description: 'fetch favourites list',
      },
    }
  );

export const useDeleteFavouritesCollection = () =>
  useApiMutation<void, Error>(endpoints.deleteFavouritesCollection, 'delete', {
    meta: {
      description: 'delete favourites list',
    },
  });

export const useDeleteFavouritesItem = () =>
  useApiMutation<void, Error>(endpoints.deleteFavouritesItem, 'delete', {
    meta: {
      description: 'delete favourite item',
    },
  });

export const useAddToCart = () =>
  useApiMutation<CartResponseModel, Error, AddToCartRequestModel>(
    endpoints.addProductToCart,
    'post',
    {
      meta: {
        description: 'add item to cart',
      },
    }
  );

export const useAddMultipleToCart = () =>
  useApiMutation<void, Error, AddToCartRequestModel[]>(
    endpoints.addMultipleProductsToCart,
    'post',
    {
      meta: {
        description: 'add items to cart',
      },
    }
  );

export const useValidateCart = () =>
  useApiMutation<ValidateCartResponse, AxiosError, void>(endpoints.validateCart, 'get', {
    meta: {
      description: 'validate cart',
    },
  });

export const useCheckout = () =>
  useApiMutation<CheckoutResponse, Error, void>(endpoints.checkoutCart, 'post', {
    meta: {
      description: 'checkout',
    },
  });

export const useRemoveFromCart = () =>
  useApiMutation<CartResponseModel, Error, void>(endpoints.removeFromCart, 'delete', {
    meta: {
      description: 'remove from cart',
    },
  });

export const useUpdateProductQuantity = () =>
  useApiMutation<any, Error, UpdateQuantityRequestModel>(endpoints.updateQuantity, 'put', {
    meta: {
      description: 'update product quantity',
    },
  });

export const useEditOrder = (orderId: string, enabled: boolean) =>
  useApiQuery<EditOrderResponseModel>(['editOrder', orderId], endpoints.editOrder, 'put', {
    pathParams: { orderId },
    enabled: enabled && !!orderId,
    staleTime: Infinity,
    meta: {
      description: 'edit order',
    },
  });

export const useGetCart = (enabled: boolean = true) =>
  useApiQuery<CartResponseModel>(['cart'], endpoints.getUserCart, 'get', {
    staleTime: 10000,
    enabled,
    meta: {
      description: 'fetch cart',
    },
  });

export const useUpdateBasketDetails = () =>
  useApiMutation<void, Error, UpdateBasketRequestModel>(endpoints.updateBasketDetails, 'put', {
    meta: {
      description: 'update basket details',
    },
  });

export const useCancelOrder = () =>
  useApiMutation<void, Error, void>(endpoints.cancelOrder, 'delete', {
    meta: {
      description: 'cancel order',
    },
  });

export const useAddProductToFavouritesCollection = () =>
  useApiMutation<void, Error, NewFavouritesItemRequestModel>(
    endpoints.addProductToFavouritesCollection,
    'post',
    {
      meta: {
        description: 'add product to favourites list',
      },
    }
  );

export const useEditFavouritesCollection = () =>
  useApiMutation<void, Error, UpdateFavouritesCollectionRequestModel>(
    endpoints.updateFavouritesCollection,
    'put',
    {
      meta: {
        description: 'edit favourites list',
      },
    }
  );

export const useControlFavouritesAccess = () =>
  useApiMutation<void, Error, ControlFavouritesAccessRequestModel>(
    endpoints.controlFavouritesAccess,
    'post',
    {
      meta: {
        description: 'control favourites list access',
      },
    }
  );

export const useCreateOrderIssue = () =>
  useApiMutation<{ id: string }, Error, CreateOrderIssueRequestModel>(
    endpoints.createOrderIssue,
    'post',
    {
      meta: {
        description: 'create order issue',
      },
    }
  );

export const useUploadOrderIssueImage = () =>
  useApiMutation<UploadOrderIssueImageResponse, Error, FormData>(
    endpoints.uploadOrderIssueImage,
    'post',
    {
      meta: {
        description: 'upload order issue image',
      },
    }
  );

export const useEditOrderIssue = () =>
  useApiMutation<void, Error, UpdateOrderIssueRequestModel>(endpoints.updateOrderIssue, 'put', {
    meta: {
      description: 'edit order issue',
    },
  });

export const useCreateOrderIssueReply = () =>
  useApiMutation<{ id: string; issueId: string }, Error, CreateOrderIssueReplyModel>(
    endpoints.createOrderIssueReply,
    'post',
    {
      meta: {
        description: 'create order issue reply',
      },
    }
  );

export const useGetOrders = (paginationModel: GridPaginationModel, sortModel?: GridSortItem) =>
  useApiQuery<PaginatedResult<BuyerOrderModel>>(
    ['orders', paginationModel.page, paginationModel.pageSize, sortModel],
    endpoints.getOrders,
    'get',
    {
      queryParams: {
        // +1 because datagrid is 0 based and backend starts from 1
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        sortBy: sortModel?.field,
        sortOrder: sortModel?.sort?.toUpperCase(),
      },
      meta: {
        description: 'fetch orders',
      },
    }
  );

export const useGetGenerationHistory = (
  paginationModel: GridPaginationModel,
  sortModel?: GridSortItem
) =>
  useApiQuery<PaginatedResult<GenerationHistoryItem>>(
    ['generation-history', paginationModel.page, paginationModel.pageSize, sortModel],
    endpoints.getGenerationHistory,
    'get',
    {
      queryParams: {
        // +1 because datagrid is 0 based and backend starts from 1
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        sortBy: sortModel?.field,
        sortOrder: sortModel?.sort?.toUpperCase(),
      },
      meta: {
        description: 'fetch generation history',
      },
    }
  );

export const useExportPurchaseOrders = () =>
  useApiMutation<string, AxiosError, void, { queryParams: { dateFrom?: string; dateTo?: string } }>(
    endpoints.exportPurchaseOrders,
    'get',
    {
      meta: {
        description: 'export purchase orders',
      },
    }
  );

export const useExportGoodsReceivedNotes = () =>
  useApiMutation<string, AxiosError, void, { queryParams: { dateFrom?: string; dateTo?: string } }>(
    endpoints.exportGoodsReceivedNotes,
    'get',
    {
      meta: {
        description: 'export goods received notes',
      },
    }
  );

export const useExportCreditNoteRequests = () =>
  useApiMutation<string, AxiosError, void, { queryParams: { dateFrom?: string; dateTo?: string } }>(
    endpoints.exportCreditNoteRequests,
    'get',
    {
      meta: {
        description: 'export credit note requests',
      },
    }
  );

export const useGetProductDetails = (stockId: string | null) =>
  useApiQuery<ProductDetailsModel>(
    ['productDetails', { stockId }],
    endpoints.getProductDetails,
    'get',
    {
      pathParams: {
        stockId,
      },
      enabled: !!stockId,
      select: (data) => {
        const allowedAllergens = [
          'Celery',
          'Crustacea',
          'Eggs',
          'Fish',
          'Lupin',
          'Milk',
          'Molluscs',
          'Mustard',
          'Peanuts',
          'Sesame Seeds',
          'Soya',
          'Sulphur Dioxide',
          'Tree Nuts',
          'Gluten',
        ];

        return {
          ...data,
          attributes: {
            ...data.attributes,
            containsAllergens:
              data.attributes.containsAllergens?.filter((allergen) =>
                allowedAllergens.includes(allergen)
              ) ?? [],
            mayContainAllergens:
              data.attributes.mayContainAllergens?.filter((allergen) =>
                allowedAllergens.includes(allergen)
              ) ?? [],
          },
        };
      },
      meta: {
        description: 'fetch product details',
      },
    }
  );

export const useGetOrderDetails = (orderId: string, sortModel?: GridSortItem) =>
  useApiQuery<BuyerOrderModel>(
    ['orderDetails', { id: orderId }, sortModel],
    endpoints.getOrderDetails,
    'get',
    {
      pathParams: {
        id: orderId,
      },
      queryParams: {
        sortBy: sortModel?.field,
        sortOrder: sortModel?.sort?.toUpperCase(),
      },
      meta: {
        description: 'fetch order details',
      },
      enabled: !!orderId,
    }
  );

export const useDownloadOrderPdf = () =>
  useApiMutation<Blob, AxiosError, void>(endpoints.downloadOrderPdf, 'get', {
    responseType: 'blob',
    meta: {
      description: 'download order PDF',
    },
  });

export const useGetOrderIssuesForOrder = (orderId: string) =>
  useApiQuery<OrderIssueModel[]>(
    ['orderIssuesForOrder', { id: orderId }],
    endpoints.getOrderIssuesForOrder,
    'get',
    {
      pathParams: {
        id: orderId,
      },
      meta: {
        description: 'fetch order issues for order',
      },
      enabled: !!orderId,
    }
  );

export const useGetOrderIssueById = (orderId: string, issueId: string) =>
  useApiQuery<OrderIssueModel>(
    ['orderIssueById', { id: orderId, issueId }],
    endpoints.getOrderIssueById,
    'get',
    {
      pathParams: {
        id: orderId,
        issueId,
      },
      meta: {
        description: 'fetch order issue',
      },
      enabled: !!orderId && !!issueId,
    }
  );

export const useAddWeightToProduct = () =>
  useApiMutation<{ orderId: string; orderProductId: string }, Error, { weight: number }>(
    endpoints.addWeightToProduct,
    'post',
    {
      meta: {
        description: 'add weight to product',
      },
    }
  );

export const useGetOrderIssuesLog = (
  paginationModel: MRT_PaginationState,
  sortModel?: GridSortItem
) =>
  useApiQuery<PaginatedResult<OrderIssueLogModel>>(
    [
      'orderIssuesLog',
      paginationModel.pageIndex,
      paginationModel.pageSize,
      sortModel?.field,
      sortModel?.sort,
    ],
    endpoints.getOrderIssuesLog,
    'get',
    {
      queryParams: {
        page: paginationModel.pageIndex + 1,
        pageSize: paginationModel.pageSize,
        sortBy: sortModel?.field,
        sortOrder: sortModel?.sort?.toUpperCase(),
      },
      meta: {
        description: 'fetch order issues log',
      },
    }
  );

export const useGetProducers = (
  buyerOrgId: string | null,
  buyerSiteId: string | null,
  paginationModel: GridPaginationModel
) =>
  useApiQuery<PaginatedResult<ProducerListItem>>(
    ['producers', paginationModel.page, paginationModel.pageSize],
    endpoints.getProducers,
    'get',
    {
      pathParams: {
        buyerOrgId,
        buyerSiteId,
      },
      queryParams: {
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      },
      enabled: !!buyerOrgId && !!buyerSiteId,
      meta: {
        description: 'fetch producers',
      },
    }
  );

export const useGetProducerDetails = (producerId: string | null) =>
  useApiQuery<IProducerDetails>(
    ['producerDetails', { producerId }],
    endpoints.getProducerDetails,
    'get',
    {
      pathParams: {
        producerId,
      },
      enabled: !!producerId,
      meta: {
        description: 'fetch producer details',
      },
    }
  );

export const useGetAllDistributors = (paginationModel: GridPaginationModel) =>
  useApiQuery<PaginatedResult<Distributor>>(
    ['distributors', paginationModel.page, paginationModel.pageSize],
    endpoints.getAllDistributors,
    'get',
    {
      queryParams: {
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      },
      meta: {
        description: 'fetch all distributors',
      },
    }
  );

export const useGetDistributorSites = (
  distributorId: string,
  paginationModel: GridPaginationModel
) =>
  useApiQuery<PaginatedResult<DistributorSite>>(
    ['distributorSites', distributorId, paginationModel.page, paginationModel.pageSize],
    endpoints.getDistributorSites,
    'get',
    {
      pathParams: { distributorId },
      queryParams: {
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      },
      enabled: !!distributorId,
      meta: {
        description: 'fetch distributor sites',
      },
    }
  );

export const useRegister = () =>
  usePublicApiMutation<{ email: string }, Error, RegisterUserRequest>(
    endpoints.registerUser,
    'post',
    {
      meta: {
        description: 'register',
      },
    }
  );

export const useEnableMfa = () =>
  usePublicApiMutation<{ totpUrl: string; mfaString: string }, Error, { email: string }>(
    endpoints.mfaEnable,
    undefined,
    {
      meta: {
        description: 'enable multi-factor authentication',
      },
    }
  );

export const useVerifyMfa = () =>
  usePublicApiMutation<void, Error, { email: string; code: string }>(endpoints.mfaVerify, 'post', {
    meta: {
      description: 'verify multi-factor authentication',
    },
  });

export const useForgotPassword = () =>
  usePublicApiMutation<void, Error, { email: string }>(endpoints.forgotPassword, 'post', {
    meta: {
      description: 'submit forgetton password',
    },
  });

export const useResetPassword = () =>
  usePublicApiMutation<void, Error, { email: string; code: string; newPassword: string }>(
    endpoints.resetPassword,
    'post',
    {
      meta: {
        description: 'reset password',
      },
    }
  );

export const useChangePassword = () =>
  useApiMutation<void, Error, { oldPassword: string; newPassword: string }>(
    endpoints.changePassword,
    'post',
    {
      meta: {
        description: 'change password',
      },
    }
  );

export const useGetAllNewsPosts = (paginationModel: GridPaginationModel) =>
  useApiQuery<PaginatedResult<NewsPostModel>>(
    ['newsPosts', paginationModel.page, paginationModel.pageSize],
    endpoints.getAllNewsPosts,
    'get',
    {
      queryParams: {
        page: paginationModel.page + 1, // Convert 0-indexed model to 1-indexed API
        pageSize: paginationModel.pageSize,
      },
      meta: {
        description: 'fetch all news posts',
      },
    }
  );

export const useGetNewsPostById = (postId: string | null) =>
  useApiQuery<NewsPostModel>(['newsPost', { id: postId }], endpoints.newsPostById, 'get', {
    pathParams: {
      id: postId,
    },
    enabled: !!postId,
    meta: {
      description: 'fetch news post',
    },
  });

export const useGetProducersByBuyerSite = (buyerOrgId: string, buyerSiteId: string) =>
  useApiQuery<{ data: Array<{ id: string; name: string }> }>(
    ['producers', buyerOrgId, buyerSiteId],
    endpoints.getProducersByBuyerSite,
    'get',
    {
      pathParams: {
        buyerOrgId,
        buyerSiteId,
      },
      queryParams: {
        page: 1,
        pageSize: 10,
        sortBy: 'name',
        sortOrder: 'ASC',
      },
      enabled: !!buyerOrgId && !!buyerSiteId,
      meta: {
        description: 'fetch producers by buyer site',
      },
    }
  );

export const useGetProductTemplatesByProducer = (producerId: string | null) =>
  useApiQuery<Array<{ id: string; name: string }>>(
    ['productTemplates', producerId],
    endpoints.getProductTemplatesByProducer,
    'get',
    {
      pathParams: {
        producerId,
      },
      enabled: !!producerId,
      meta: {
        description: 'fetch product templates by producer',
      },
    }
  );

export const useGenerateStory = () =>
  useApiMutation<any, Error, { productId: string; storyLength: string }>(
    endpoints.generateStory,
    'post',
    {
      meta: {
        description: 'generate story from product data',
      },
    }
  );

export const useGetProductImages = (productId: string | null) =>
  useApiQuery<ProductImagesResponse>(
    ['productImages', productId],
    endpoints.getProductImages,
    'get',
    {
      pathParams: {
        productId,
      },
      enabled: !!productId,
      meta: {
        description: 'fetch product images',
      },
    }
  );

export const useGetOrderIssueByEmailReplyId = (emailReplyId: string) =>
  usePublicApiQuery<OrderIssueModel>(
    ['orderIssueByEmailReplyId', { emailReplyId }],
    endpoints.getOrderIssueByEmailId,
    'get',
    {
      pathParams: {
        emailReplyId,
      },
      meta: {
        description: 'fetch order issue by email reply id',
      },
      enabled: !!emailReplyId,
    }
  );

export const useCreateAnonymousOrderIssueReply = () =>
  usePublicApiMutation<{ emailReplyId: string }, AxiosError, CreateOrderIssueReplyModel | FormData>(
    endpoints.createOrderIssueReplyByEmail,
    'post',
    {
      meta: {
        description: 'create anonymous order issue reply',
      },
    }
  );

export const useSearchProductSeasonalCalendarEntries = () =>
  useApiMutation<PaginatedResult<SeasonalCalendarItem>, Error, SeasonalCalendarSearchRequest>(
    endpoints.searchSeasonalCalendar,
    undefined,
    {
      meta: {
        description: 'search seasonal calendar entries',
      },
    }
  );

export const useGetFinanceOrders = (
  paginationModel: { pageIndex: number; pageSize: number },
  sortModel?: { id: string; desc: boolean },
  filterModel?: FinanceOrdersListFilterModel
) =>
  useApiQuery<PaginatedResult<FinanceOrderListModel>>(
    ['financeOrders', paginationModel.pageIndex, paginationModel.pageSize, sortModel, filterModel],
    endpoints.getFinanceOrders,
    'get',
    {
      queryParams: {
        // +1 because MRT is 0 based and backend starts from 1
        page: paginationModel.pageIndex + 1,
        pageSize: paginationModel.pageSize,
        sortBy: sortModel?.id,
        sortOrder: sortModel ? (sortModel.desc ? 'DESC' : 'ASC') : undefined,
        distributorOrgId: filterModel?.distributorOrgId || undefined,
        distributorSiteId: filterModel?.distributorSiteId || undefined,
        buyerSiteId: filterModel?.buyerSiteId || undefined,
        // Local calendar date, not ISO timestamp — the backend compares against date
        // columns, and a midnight BST timestamp lands on the previous UTC day.
        deliveryDateFrom: filterModel?.deliveryDateFrom
          ? format(new Date(filterModel.deliveryDateFrom), 'yyyy-MM-dd')
          : undefined,
        deliveryDateTo: filterModel?.deliveryDateTo
          ? format(new Date(filterModel.deliveryDateTo), 'yyyy-MM-dd')
          : undefined,
      },
      keepPreviousData: true, // for pagination to work
      meta: {
        description: 'fetch finance orders',
      },
    }
  );

export const useGetFinanceFilterOptions = () =>
  useApiQuery<FinanceFilterOptionsModel>(
    ['financeFilterOptions'],
    endpoints.getFinanceFilterOptions,
    'get',
    {
      refetchOnWindowFocus: false,
      meta: {
        description: 'fetch finance filter options',
      },
    }
  );

export const useGetFinanceInvoiceById = (invoiceId: string | null) =>
  useApiQuery<InvoiceDetailsModel>(
    ['financeInvoiceDetails', { invoiceId }],
    endpoints.getFinanceInvoiceById,
    'get',
    {
      pathParams: { invoiceId },
      enabled: !!invoiceId,
      meta: {
        description: 'fetch finance invoice details',
      },
    }
  );

export const useGetRecommendations = (
  buyerOrgId: string | null,
  buyerSiteId: string | null,
  productTemplateId: string | null
) =>
  useApiQuery<RecommendationItemModel[]>(
    ['recommendations', buyerOrgId, buyerSiteId, productTemplateId],
    endpoints.getRecommendations,
    'get',
    {
      pathParams: { buyerOrgId, buyerSiteId, productTemplateId },
      enabled: !!buyerOrgId && !!buyerSiteId && !!productTemplateId,
      meta: { description: 'fetch product recommendations' },
    }
  );
